import Button from '@doordash/component-button'
import Loading from '@doordash/component-loading'
import { Text } from '@doordash/design-language'
import * as React from 'react'
import { connect } from 'react-redux'
import { compressImage } from '../../../../utilities/imageCompression'

import * as actions from '../../../../actions'
import * as events from '../../../../events'
import {
  Photo,
  StripeCredentials,
  StripeFileResponse,
} from '../../../../models/types'
import * as DA from '../../../../typeModules/events'
import { submitPhotoIdBack as sId } from '../strings'

import { CenteredContent, UploadPreview } from '../styles'

type Props = {
  photoIdBack: Photo
  stripeCredentials?: StripeCredentials
  stripeFileUpload?: StripeFileResponse
  paymentAccountId?: string
  goToNextStep: () => void
  setPhotoIdBack: (image: Photo) => void
  uploadPhoto?: (
    stripeKey: string,
    paymentAccountId: string,
    payload: object,
    side?: string
  ) => Promise<any>
  trackBankAdditionalVerification?: (
    data: DA.BankAdditionalVerification
  ) => Promise<any>
  country?: string
  errorMessage?: string
  clearErrors?: () => void
}

declare const document

export const SubmitPhotoIdBack: React.FunctionComponent<Props> = props => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  let event

  React.useEffect(() => {
    const { trackBankAdditionalVerification, country } = props

    const input = document.createElement('input')
    input.id = 'backid'
    input.type = 'file'
    input.accept = 'image/*'
    input.style.display = 'none'
    document.body.appendChild(input)

    event = async e => {
      const imageFile = e.target.files[0]
      const res = await compressImage(imageFile)

      props.setPhotoIdBack(res)

      // Must set to false before setting image
      setIsLoading(false)
      document.getElementById('uploadPreview').src = res.blob
    }

    input.addEventListener('change', event, false)

    trackBankAdditionalVerification({
      country: DA.Countries[country],
      action: DA.EventActions.Rendered,
      name: DA.BankAdditionalVerificationPages.SubmitPhotoId,
    })
  }, [])

  const handleImportPhoto = () => {
    const { trackBankAdditionalVerification, country } = props

    setIsLoading(true)

    const input = document.getElementById('backid')
    input.click()

    trackBankAdditionalVerification({
      country: DA.Countries[country],
      action: DA.EventActions.Clicked,
      name: DA.BankAdditionalVerificationPages.SubmitPhotoIdBack,
      data: 'handle_import_photo',
    })
  }

  const handleSubmit = async () => {
    const {
      photoIdBack,
      uploadPhoto,
      stripeCredentials,
      paymentAccountId,
      goToNextStep,
      trackBankAdditionalVerification,
      country,
    } = props

    setIsSubmitting(true)

    const payload = new FormData()
    payload.append('file', photoIdBack.file)
    payload.append('purpose', 'identity_document')

    try {
      await uploadPhoto(
        stripeCredentials.publicKey,
        paymentAccountId,
        payload,
        'back'
      )

      trackBankAdditionalVerification({
        country: DA.Countries[country],
        action: DA.EventActions.Submit,
        name: DA.BankAdditionalVerificationPages.SubmitPhotoIdBack,
      })
    } catch (e) {
      setIsSubmitting(false)

      trackBankAdditionalVerification({
        country: DA.Countries[country],
        action: DA.EventActions.Error,
        name: e,
      })

      return
    }

    const input = document.getElementById('backid')
    input.removeEventListener('change', event, false)

    goToNextStep()
  }

  return (
    <CenteredContent>
      <Text
        styles={Text.Styles.PageTitle}
        textAlign={Text.TextAlignments.Center}
        tag="h1"
      >
        {sId.title()}
      </Text>
      <Text
        styles={Text.Styles.PageDescriptionBody}
        textAlign={Text.TextAlignments.Center}
        tag="h2"
      >
        {sId.description()}
      </Text>
      {isLoading ? <Loading /> : <UploadPreview id="uploadPreview" />}
      {props.errorMessage && <Text>{props.errorMessage}</Text>}
      <Button
        onClick={props.photoIdBack ? handleSubmit : handleImportPhoto}
        type={Button.Types.Primary}
        state={isSubmitting ? Button.States.Loading : Button.States.Default}
      >
        {props.photoIdBack ? sId.primaryAction_2() : sId.primaryAction_1()}
      </Button>
      {props.photoIdBack && (
        <Button type={Button.Types.FlatPrimary} onClick={handleImportPhoto}>
          {sId.secondaryAction()}
        </Button>
      )}
      {props.errorMessage && (
        <Button type={Button.Types.FlatPrimary} onClick={props.goToNextStep}>
          {sId.maybeLater()}
        </Button>
      )}
    </CenteredContent>
  )
}

const mapStateToProps = state => ({
  country: state.application.country,
  paymentAccountId: state.paymentAccount.id,
  stripeCredentials: state.stripeCredentials,
  stripeFileUpload: state.stripeFileUpload,
  errorMessage: state.errorMessage,
})

const mapDispatchToProps = {
  uploadPhoto: actions.uploadPhoto,
  trackBankAdditionalVerification: events.trackBankAdditionalVerification,
  clearErrors: actions.clearErrors,
}

export default connect(mapStateToProps, mapDispatchToProps)(SubmitPhotoIdBack)
